import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet, HelmetProvider } from "react-helmet-async";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Section Components
import {
  Navigation,
  Hero,
  FromWaldrechner,
  EntrySteps,
  ExampleCase,
  WhatYouGet,
  TargetAudience,
  NotReplacement,
  FAQ,
  FinalCTA,
  Footer,
} from "@/components/sections";

// Layout Components
import { CookieConsent } from "@/components/layout/CookieConsent";
import { ChatAssistant } from "@/components/layout/ChatAssistant";

// Forms
import { DemoRequestModal } from "@/components/forms/DemoRequestModal";

// Pages
import { DemoPage } from "@/components/pages/DemoPage";

// Legal Pages
import { ImpressumPage } from "@/pages/legal/ImpressumPage";
import { DatenschutzPage } from "@/pages/legal/DatenschutzPage";
import { NutzungsbedingungenPage } from "@/pages/legal/NutzungsbedingungenPage";

// Landing Page Component
const LandingPage = () => {
  const { t } = useTranslation();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const openDemoModal = () => setIsDemoModalOpen(true);
  const closeDemoModal = () => setIsDemoModalOpen(false);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <meta name="keywords" content="Forstfall, Entscheidungshilfe, Waldbesitzer, Forstberater, Stolpersteine, offene Punkte, Vorentscheidung, Forstvorhaben" />
        <link rel="canonical" href="https://www.foresthub.app/" />
        <meta property="og:title" content={t('meta.ogTitle')} />
        <meta property="og:description" content={t('meta.ogDescription')} />
        <meta property="og:url" content="https://www.foresthub.app/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ForestHub" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.ogTitle')} />
        <meta name="twitter:description" content={t('meta.ogDescription')} />
      </Helmet>

      <Navigation onRequestDemo={openDemoModal} />
      
      <main>
        <Hero />
        <FromWaldrechner />
        <EntrySteps />
        <ExampleCase />
        <WhatYouGet />
        <TargetAudience />
        <NotReplacement />
        <FAQ />
        <FinalCTA />
      </main>

      <Footer />
      <ChatAssistant />
      <CookieConsent />
      <DemoRequestModal isOpen={isDemoModalOpen} onClose={closeDemoModal} />
    </div>
  );
};

// Main App Component
function App() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const openDemoModal = () => setIsDemoModalOpen(true);
  const closeDemoModal = () => setIsDemoModalOpen(false);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/demo" element={<DemoPage onRequestDemo={openDemoModal} />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />
          </Routes>
          <DemoRequestModal isOpen={isDemoModalOpen} onClose={closeDemoModal} />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
